import { toIntegerMilliseconds } from '../../../utils/urPlayerUtils'

export const AUTOPLAY_ABORT = 'player_autoplay_abort'
export const AUTOPLAY_CLICK_PLAY = 'player_autoplay_click_play'

export interface StaticEventParams {
  player: string
  program_id: number
  program_name: string
  series_id: number | null
  series_name: string | null
  media_format: string
  product_type: string
}

export type AutoPlayParams = Pick<
  StaticEventParams,
  'player' | 'media_format' | 'series_id' | 'series_name'
> & {
  program_id_previous: number
  program_name_previous: string
  program_id_next: number
  program_name_next: string
  countdown_seconds_left: number
}
export interface IDataBuilder {
  staticEventParams: {
    player: string
    program_id: number
    program_name: string
    series_id: number | null
    series_name: string | null
    media_format: string
    product_type: string
  }
  autoplay: boolean
  getCommonEventParams: () => {
    position: number
  } & StaticEventParams
  getAutoplayParams: (arg: NextEpisodeEvent) => AutoPlayParams

  getPosition: () => number

  typicalAgeRangeTranslation: string
}
export interface NextEpisodeEvent {
  programIdNext: number
  programNameNext: string
  countdownSecondsLeft: number
}

type Events = InstanceType<typeof EventPayloadFactory>['factories']
type EventName = keyof Events

type SeekEvent = { position: number; offset: number }
type SubtitlesEvent = { from: string; to: string }
type QualityEvent = { quality: string }

interface CustomEventParams {
  event: string
  typical_age_range_translation: string
  [key: string]: unknown
}

type Factories = Record<
  string,
  (
    payload:
      | SeekEvent
      | SubtitlesEvent
      | NextEpisodeEvent
      | QualityEvent
      | undefined,
  ) => StaticEventParams | AutoPlayParams | CustomEventParams
>

export type EventData =
  | ReturnType<Events[EventName]>
  | SeekEvent
  | SubtitlesEvent
  | QualityEvent

export class EventPayloadFactory {
  private readonly factories: Factories

  constructor(private readonly dataBuilder: IDataBuilder) {
    this.factories = {
      player_play: () => ({
        autoplay: dataBuilder.autoplay,
        ...dataBuilder.getCommonEventParams(),
      }),
      player_pause: dataBuilder.getCommonEventParams,
      player_resume: dataBuilder.getCommonEventParams,
      player_start: () => ({
        autoplay: dataBuilder.autoplay,
        ...dataBuilder.getCommonEventParams(),
      }),
      player_rewind: () => ({
        ...dataBuilder.staticEventParams,
      }),
      player_buffering: dataBuilder.getCommonEventParams,
      player_abort_stream: dataBuilder.getCommonEventParams,
      player_seek: (
        payload:
          | SeekEvent
          | NextEpisodeEvent
          | SubtitlesEvent
          | QualityEvent
          | undefined,
      ) => {
        if (payload && 'position' in payload) {
          return {
            ...dataBuilder.staticEventParams,
            position_from: toIntegerMilliseconds(payload.position),
            position_to: toIntegerMilliseconds(payload.offset),
          }
        } else {
          return {
            ...dataBuilder.staticEventParams,
            position_from: 0,
            position_to: 0,
          }
        }
      },
      player_change_subtitles: (
        payload:
          | SubtitlesEvent
          | SeekEvent
          | NextEpisodeEvent
          | QualityEvent
          | undefined,
      ) => ({
        ...dataBuilder.getCommonEventParams(),
        subtitles_from: payload && 'from' in payload ? payload.from : '',
        subtitles_to: payload && 'to' in payload ? payload.to : '',
      }),
      [AUTOPLAY_ABORT]: (
        nextEpisodeEvent:
          | NextEpisodeEvent
          | SeekEvent
          | SubtitlesEvent
          | QualityEvent
          | undefined,
      ) => ({
        ...dataBuilder.getAutoplayParams(nextEpisodeEvent as NextEpisodeEvent),
      }),
      [AUTOPLAY_CLICK_PLAY]: (
        nextEpisodeEvent:
          | NextEpisodeEvent
          | SeekEvent
          | SubtitlesEvent
          | QualityEvent
          | undefined,
      ) => ({
        ...dataBuilder.getAutoplayParams(nextEpisodeEvent as NextEpisodeEvent),
      }),
      player_step_backward: (
        payload:
          | SeekEvent
          | NextEpisodeEvent
          | SubtitlesEvent
          | QualityEvent
          | undefined,
      ) => {
        if (payload && 'position' in payload) {
          return {
            ...dataBuilder.staticEventParams,
            position_from: toIntegerMilliseconds(payload.position),
            position_to: toIntegerMilliseconds(payload.offset),
          }
        } else {
          return {
            ...dataBuilder.staticEventParams,
            position_from: 0,
            position_to: 0,
          }
        }
      },
      player_step_forward: (
        payload:
          | SeekEvent
          | NextEpisodeEvent
          | SubtitlesEvent
          | QualityEvent
          | undefined,
      ) => {
        if (payload && 'position' in payload) {
          return {
            ...dataBuilder.staticEventParams,
            position_from: toIntegerMilliseconds(payload.position),
            position_to: toIntegerMilliseconds(payload.offset),
          }
        } else {
          return {
            ...dataBuilder.staticEventParams,
            position_from: 0,
            position_to: 0,
          }
        }
      },
      player_enter_fullscreen: dataBuilder.getCommonEventParams,
      player_exit_fullscreen: dataBuilder.getCommonEventParams,
      player_change_quality: (
        payload:
          | QualityEvent
          | NextEpisodeEvent
          | SeekEvent
          | SubtitlesEvent
          | undefined,
      ) => ({
        quality: payload && 'quality' in payload ? payload.quality : '',
        ...dataBuilder.getCommonEventParams(),
      }),
      player_toggle_chapters: dataBuilder.getCommonEventParams,
    }
  }

  getEvent = (
    eventName: EventName,
    payload?: Parameters<Events[EventName]>[0],
  ) => {
    return {
      event: eventName,
      typical_age_range_translation:
        this.dataBuilder.typicalAgeRangeTranslation,
      //  Typescript cannot infer correct payload in this scope.
      //  Therefore it is cast as any.
      ...this.factories[eventName](payload),
    }
  }

  getTimeEvent = (eventName: string) => ({
    event: eventName,
    typical_age_range_translation: this.dataBuilder.typicalAgeRangeTranslation,
    ...this.dataBuilder.staticEventParams,
  })
}

export default EventPayloadFactory
